import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';
import { api } from '@/api/api-client.config.ts';
import { QueryConfig } from '@/api/react-query.config';
import { TournamentLeaderboardData } from '@/@types/api.types.ts';

type GetLeaderboardProps = {
  leagueId: number;
  tournamentId: number;
  offset: number;
  limit: number;
};

const getLeaderboard = ({
  leagueId,
  tournamentId,
  offset,
  limit,
}: GetLeaderboardProps): Promise<TournamentLeaderboardData> =>
  api.get(`/admin/league-tournament-leaderboard/${leagueId}`, {
    params: {
      tournament_id: tournamentId,
      offset,
      limit,
    },
  });

const getInfiniteTournamentLeaderboardOptions = ({
  leagueId,
  tournamentId,
  offset,
  limit,
}: GetLeaderboardProps) => {
  return infiniteQueryOptions({
    queryKey: [
      'getLeagueTournamentLeaderboard',
      leagueId,
      tournamentId,
      offset,
      limit,
    ],
    queryFn: ({ pageParam = 0 }) =>
      getLeaderboard({
        leagueId,
        tournamentId,
        offset: pageParam * limit,
        limit,
      }),
    //* This is for when we start doing pagination properly
    getNextPageParam: (lastPage, allPages) => {
      return lastPage?.leaderboard?.length > 0 ? allPages?.length : undefined;
    },
    initialPageParam: 0,
    select: (data) => {
      // Merge leaderboard data from each page into a single array.
      // Keep all other properties as they are on page 0.
      if (!data?.pages?.length) return null;
      const firstPage = data.pages[0];
      const mergedLeaderboard = data.pages.flatMap(
        (page) => page.leaderboard || []
      );
      return {
        ...firstPage,
        leaderboard: mergedLeaderboard,
      };
    },
  });
};

type UseGetLeagueTournamentLeaderboardProps = GetLeaderboardProps & {
  queryConfig?: QueryConfig<typeof getLeaderboard>;
};

export const useGetLeagueTournamentLeaderboard = ({
  leagueId,
  tournamentId,
  offset,
  limit,
}: UseGetLeagueTournamentLeaderboardProps) => {
  return useInfiniteQuery({
    ...getInfiniteTournamentLeaderboardOptions({
      leagueId,
      tournamentId,
      offset,
      limit,
    }),
    // Refetch every minute
    refetchInterval: 1000 * 60,
    enabled: Boolean(leagueId) && Boolean(tournamentId),
  });
};
